import { useEffect } from 'react'

export interface QA {
  q: string
  /** Plain-text answer — MUST match the visible answer verbatim (Google requires JSON-LD == visible). */
  a: string
}

// Injects a FAQPage JSON-LD block whose text mirrors the visible Q&A EXACTLY, then removes it on
// unmount so SPA route changes don't leave a stale/duplicate FAQ schema on the page. The `id` keys
// the <script> so two FAQ sections on one page (shouldn't happen, but safe) don't collide. The effect
// keys on the serialized JSON, not the `items` array reference, so callers that build a fresh array
// each render (e.g. carrierFaq(c)) don't thrash the DOM when the content is unchanged.
function useFaqJsonLd(id: string, items: QA[]) {
  const json = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  })
  useEffect(() => {
    const el = document.createElement('script')
    el.type = 'application/ld+json'
    el.dataset.faq = id
    el.textContent = json
    document.head.appendChild(el)
    return () => {
      el.remove()
    }
  }, [id, json])
}

// Visible, answer-shaped Q&A block + matching FAQPage structured data. The visible <p> and the
// JSON-LD answer are rendered from the SAME `a` string, so they can never drift apart.
export function Faq({
  id,
  items,
  heading = 'Frequently asked questions',
}: {
  id: string
  items: QA[]
  heading?: string
}) {
  useFaqJsonLd(id, items)
  return (
    <section className="mt-20 max-w-3xl mx-auto" aria-labelledby={`faq-${id}`}>
      <h2 id={`faq-${id}`} className="font-display font-bold text-2xl mb-8 text-center">
        {heading}
      </h2>
      <div className="space-y-4">
        {items.map((it) => (
          <div key={it.q} className="bg-surface-0 border border-border rounded-xl p-5">
            <h3 className="font-medium mb-1.5">{it.q}</h3>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{it.a}</p>
          </div>
        ))}
      </div>
    </section>
  )
}
